import type { CheckPermissionFn } from '@/components/InvoiceReportDetailDrawer/type';
import type { AdminInvoiceReportSummaryError } from '@/context/services/admin_invoice_reports/AdminInvoiceReportSummary.service';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useEffect } from 'react';

const TOAST_DURATION = 3;
const FORBIDDEN = 403;
const NOT_FOUND = 404;

const parseStatus = (
  error: AdminInvoiceReportSummaryError | undefined
): number | undefined => {
  if (!error?.code) return undefined;
  const n = Number.parseInt(error.code, 10);
  return Number.isFinite(n) ? n : undefined;
};

type Params = {
  error?: AdminInvoiceReportSummaryError;
  onClose: () => void;
  onListRefetch: CheckPermissionFn;
};

export const useAdminInvoiceReportSummaryAccessErrorEffect = ({
  error,
  onClose,
  onListRefetch,
}: Params): void => {
  const notify = useToastAPI();
  const { t } = useTranslation();

  useEffect(() => {
    const status = parseStatus(error);
    if (!error || status === undefined) return;

    if (status === FORBIDDEN) {
      // Admin role has no per-report ownership scoping: any 403 means the user
      // shouldn't be on this page. Reload lets Rails do the role-based redirect
      // (home for Approver/General, office list for No-Authority) and surface
      // the appropriate flash toast.
      location.reload();
      return;
    }

    if (status === NOT_FOUND) {
      notify.error({
        title: error.messages?.join('\n') || t('system_error'),
        duration: TOAST_DURATION,
      });
      onClose();
      onListRefetch();
    }
  }, [error, onClose, onListRefetch, notify, t]);
};
